import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import { DiscrepancyService } from "@services/ingest/discrepancyService";
import type { Discrepancy, DiscrepancyStatus } from "@models/Discrepancy.model";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { navigate } from "@/navigate";
import { Paths } from "@/paths";
import ReportDiscrepancyDialog from "./ReportDiscrepancyDialog";

const HEADER_SX = {
  fontWeight: 600,
  color: "#8c9097",
  fontSize: 12,
  textTransform: "uppercase",
  letterSpacing: 0.3,
} as const;

const STATUS_CHIP: Record<DiscrepancyStatus, "warning" | "default" | "success"> = {
  open: "warning",
  accepted: "default",
  resolved: "success",
};

export const formatDay = (value: string | null) =>
  value ? new Date(value.length === 10 ? `${value}T00:00:00` : value)
    .toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—";

/*
    Data issues, every client (MPN-2009): discrepancies the platform found when a file
    dropped people, and ones Ops reported. Open until accepted as is (with a reason) or
    resolved by a correction. The unmatched-rows Slack alert deep-links to
    /data-issues/report?population_segment_id=…&ingest_run_id=…&reason=… to open the form
    prefilled.
*/
export default function DataIssuesPage({ report = false }: { report?: boolean }) {
  useDocumentTitle("Data issues");
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<DiscrepancyStatus>("open");
  const [rows, setRows] = useState<Discrepancy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reportOpen, setReportOpen] = useState(report);

  useEffect(() => {
    setLoading(true);
    new DiscrepancyService()
      .list({ status })
      .then(setRows)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load data issues"))
      .finally(() => setLoading(false));
  }, [status]);

  const prefillSegment = Number(searchParams.get("population_segment_id")) || null;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>Data issues</Typography>
          <Typography sx={{ color: "#8c9097", fontSize: 13 }}>
            Every client, every population. Found by the platform or reported by Ops.
          </Typography>
        </Box>
        <Button variant="contained" sx={{ ml: "auto", textTransform: "none" }} onClick={() => setReportOpen(true)}>
          Report a discrepancy
        </Button>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

      <ToggleButtonGroup size="small" exclusive value={status} sx={{ mb: 2 }}
        onChange={(_, value) => value && setStatus(value)}>
        <ToggleButton value="open" sx={{ textTransform: "none" }}>Open</ToggleButton>
        <ToggleButton value="accepted" sx={{ textTransform: "none" }}>Accepted</ToggleButton>
        <ToggleButton value="resolved" sx={{ textTransform: "none" }}>Resolved</ToggleButton>
      </ToggleButtonGroup>

      {loading ? <LinearProgress /> : (
        <TableContainer sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #f3f3f3" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f9fafb" }}>
                <TableCell sx={HEADER_SX}>Found</TableCell>
                <TableCell sx={HEADER_SX}>Client · population</TableCell>
                <TableCell sx={HEADER_SX}>What's wrong</TableCell>
                <TableCell sx={HEADER_SX}>File</TableCell>
                <TableCell sx={HEADER_SX}>Found by</TableCell>
                <TableCell sx={HEADER_SX}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((d) => (
                <TableRow key={d.id} hover>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDay(d.create_date)}</TableCell>
                  <TableCell>
                    <Box sx={{ fontWeight: 600 }}>{d.organization_name}</Box>
                    <Box sx={{ color: "#64748b", fontSize: 12 }}>{d.population_name ?? "—"}</Box>
                  </TableCell>
                  <TableCell>
                    <Link to={`${import.meta.env.BASE_URL}${Paths.DATA_ISSUES}/${d.id}`}>{d.reason}</Link>
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{d.file?.file_name ?? "—"}</TableCell>
                  <TableCell>{d.source === "system" ? "Platform" : d.created_by ?? "Ops"}</TableCell>
                  <TableCell>
                    <Chip size="small" label={d.status} color={STATUS_CHIP[d.status]} />
                    {d.holds_file && <Chip size="small" label="file held" color="error" sx={{ ml: 0.5 }} />}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {rows.length === 0 && (
            <Box sx={{ p: 4, textAlign: "center", color: "#8c9097" }}>
              <Typography>Nothing {status}.</Typography>
            </Box>
          )}
        </TableContainer>
      )}

      <Typography sx={{ color: "#8c9097", fontSize: 12, mt: 2 }}>
        Accepting says "this is fine as is" and needs a reason. Resolving means a correction was
        added. Both stay in the history.
      </Typography>

      <ReportDiscrepancyDialog
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        onSaved={(d) => navigate(`${import.meta.env.BASE_URL}${Paths.DATA_ISSUES}/${d.id}`)}
        populationSegmentId={report ? prefillSegment : null}
        ingestRunId={report ? Number(searchParams.get("ingest_run_id")) || null : null}
        reason={report ? searchParams.get("reason") ?? "" : ""}
      />
    </Box>
  );
}
